import { memo, useCallback, useEffect, useState } from "react";
import Select from "react-select";
import getOr from "lodash/fp/getOr";
import get from "lodash/fp/get";
import CustomOption from "./components/CustomOption";
import CustomSingleValue from "./components/CustomSingleValue";
import "./CustomAutoComplete.css";

const CustomAutoComplete = ({
  initOption,
  otherValue,
  options,
  placeholder,
  label,
  customStyles = {},
  menuPlacement,
  isClearable = true,
  isSearchable = true,
  overrideSelectProps = {},
  customLabelStyles = {},
  onChange = () => {},
  onBlur = () => {}, // added
  error = "", // added
  touched = false, // added
  disabled = false,
  menuHeight = 300,
}) => {
  const [value, setValue] = useState();

  const handleChange = useCallback(
    (selected) => {
      setValue(selected);
      onChange(selected);
    },
    [onChange]
  );

  useEffect(() => {
    if (get("value", initOption)) {
      setValue(initOption);
    }
  }, [initOption]);

  useEffect(() => {
    if (otherValue === "" || otherValue === null) {
      setValue(undefined);
      handleChange(undefined);
    }
  }, [otherValue, handleChange]);

  const getBoxShadow = (isFocused, hasError) => {
    if (hasError) return "0 0 0 1px red";
    return isFocused ? "0 0 0 1px #efefef" : "0 0 0 1px #efefef";
  };

  const styles = {
    control: (base, state) => ({
      ...base,
      border: "none",
      width: "100%",
      borderRadius: "100px",
      boxShadow: getBoxShadow(state.isFocused, touched && !!error),
      transition: "box-shadow ease 0.25s",
      paddingLeft: "3px",
      margin: 0,
      outline: "none",
      "&:hover": {
        border: "none",
      },
      ...getOr({}, "control", customStyles),
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "7px 0 7px 14px",
      margin: 0,
      ...getOr({}, "valueContainer", customStyles),
    }),
    menu: (base) => ({
      ...base,
      minWidth: 220,
      zIndex: 2,
    }),
    placeholder: (base) => ({
      ...base,
      fontFamily: "Noto Sans HK, sans-serif",
      ...getOr({}, "placeholder", customStyles),
    }),
    noOptionsMessage: (base) => ({
      ...base,
      fontFamily: "Noto Sans HK, sans-serif",
      fontSize: 14,
      ...getOr({}, "noOptionsMessage", customStyles),
    }),
    loadingMessage: (base) => ({
      ...base,
      fontFamily: "Noto Sans HK, sans-serif",
      fontSize: 14,
      ...getOr({}, "loadingMessage", customStyles),
    }),
    singleValue: (base) => ({
      ...base,
      fontFamily: "Noto Sans HK, sans-serif",
      fontSize: 14,
      ...getOr({}, "singleValue", customStyles),
    }),
    option: (base, state) => {
      const selectedStyles = state.isSelected
        ? {
            backgroundColor: "white",
            color: "blue",
          }
        : {};

      return {
        ...base,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        fontFamily: "Noto Sans HK, sans-serif",
        fontSize: 14,
        ...selectedStyles,
        ...getOr({}, "option", customStyles),
      };
    },
  };

  return (
    <div className="customautocomplete">
      {label && (
        <label
          style={{
            color: "#777",
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "24px",
            ...customLabelStyles,
          }}
        >
          {label}
        </label>
      )}
      <Select
        value={value}
        options={options}
        onChange={handleChange}
        onBlur={onBlur}
        menuPlacement={menuPlacement}
        maxMenuHeight={menuHeight}
        noOptionsMessage={() => "no_options"}
        isClearable={isClearable}
        isSearchable={isSearchable}
        placeholder={placeholder}
        components={{
          IndicatorSeparator: null,
          SingleValue: (props) => (
            <CustomSingleValue csvProps={props} otherValue={otherValue} />
          ),
          Option: CustomOption,
        }}
        styles={styles}
        isDisabled={disabled}
        {...overrideSelectProps}
      />
      {touched && error && (
        <div style={{ color: "red", marginTop: "4px", fontSize: "12px" }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default memo(CustomAutoComplete);
